/**
 * Canonical line_item_type vocabulary — mirrors the backend `line_item_type`
 * enum enforced at the DB level.
 *
 * Single source of truth for the frontend. Import from here instead of
 * duplicating line item type string literals in individual files.
 *
 * NOTE: `casing_running` is NOT a line item type. It is the name of the
 * Casing Running vertical (see `src/lib/verticals.ts`). Line items that used
 * to carry `casing_running` were migrated to `crt`. Do not reintroduce it
 * into any line item type list.
 */

/** All line item types the backend recognises. */
export type LineItemType =
  | 'power_tongs'
  | 'crt'
  | 'torque_monitoring'
  | 'thread_wash'
  | 'thread_supervision'
  | 'removing_caps'
  | 'drift'
  | 'laser_tally'
  | 'bail_extension'
  | 'day_rate'
  | 'standby'
  | 'hourly'
  | 'mobilization'
  | 'demobilization'
  | 'equipment_rental'
  | 'overtime'
  | 'subsistence'
  | 'shop'
  | 'wash_truck'
  | 'jacks'
  | 'catwalk'
  | 'other';

/**
 * Every line item type, in display order: field/service work first, then the
 * commercial + catch-all types. Any UI that enumerates line item types
 * (pickers, pay rate tables) should iterate this so nothing is silently
 * unreachable.
 *
 * This order is also the Postgres enum's sort order. API migration 0327 added
 * `overtime` and `subsistence` positioned BEFORE `other` rather than appended,
 * specifically so the catch-all stays last here — EmployeeProfile.tsx iterates
 * this array directly to build the per-employee pay-rate grid, and an appended
 * value would print "Other, Rig Overtime, Subsistence".
 *
 * There is deliberately no `travel` value. Travel is `mobilization` (outbound)
 * and `demobilization` (return) — the API rejects `travel` with 22P02.
 *
 * Mirrors fieldiq-api/src/constants/line-item-types.ts. Neither repo can read
 * the other in CI, so line-item-types.test.ts pins this array against the same
 * literal list the API's line-item-type-enum.test.ts pins against the live
 * enum. A change on one side needs the matching change on the other.
 */
export const LINE_ITEM_TYPES: readonly LineItemType[] = [
  'power_tongs',
  'crt',
  'torque_monitoring',
  'thread_wash',
  'thread_supervision',
  'removing_caps',
  'drift',
  'laser_tally',
  'bail_extension',
  'day_rate',
  'standby',
  'hourly',
  'mobilization',
  'demobilization',
  'equipment_rental',
  'overtime',
  'subsistence',
  // API migration 0336. Shop time — hours no ticket accounts for. NOT `hourly`:
  // on TNT that type means a share of an hourly-BILLED line for 6 of its 9 rows
  // and a wage for the other 3, so pricing shop work through it would pay two
  // thirds of that crew a percentage of a line item a shop shift does not have.
  'shop',
  // API migration 0360. Billing-side counterparts of the jacks / catwalk /
  // wash truck job types. Placed BEFORE `other` like 0327/0336.
  'wash_truck',
  'jacks',
  'catwalk',
  'other',
] as const;

/** Human-readable labels keyed by LineItemType. */
export const LINE_ITEM_TYPE_LABELS: Record<LineItemType, string> = {
  power_tongs: 'Power Tongs',
  crt: 'CRT (Casing Running Tool)',
  torque_monitoring: 'Torque Monitoring',
  thread_wash: 'Thread Wash',
  thread_supervision: 'Thread Supervision',
  removing_caps: 'Removing Caps',
  drift: 'Drift',
  laser_tally: 'Laser Tally',
  bail_extension: 'Bail Extension',
  day_rate: 'Day Rate',
  standby: 'Standby',
  hourly: 'Hourly',
  mobilization: 'Mobilization',
  demobilization: 'Demobilization',
  equipment_rental: 'Equipment Rental',
  // "Rig Overtime", not "Overtime": this is a CUSTOMER-BILLED line item
  // (TNT: "Tongs Overtime after 24 hr", $150/hr) that a hand takes a
  // percentage of — not a labour-law multiplier. The Shop row on the same
  // pay-rate screen carries an "hours count toward overtime" switch, and the
  // bare word invited the office to read the two as one thing. Display only;
  // the enum value and every stored `line_item_type` stay `overtime`.
  overtime: 'Rig Overtime',
  subsistence: 'Subsistence',
  shop: 'Shop',
  wash_truck: 'Wash Truck',
  jacks: 'Jacks',
  catwalk: 'Catwalk',
  other: 'Other',
};

/**
 * Legacy values that no longer exist in the enum, mapped to their canonical
 * replacement. Kept so historical rows fetched from the API still render a
 * sensible label instead of falling through to the raw string.
 */
const LEGACY_LINE_ITEM_TYPE_ALIASES: Record<string, LineItemType> = {
  casing_running: 'crt',
  // The two pre-merge drift spellings both resolve to the single `drift` value.
  drift_gauge: 'drift',
  vacuum_drifting: 'drift',
};

/** Narrow an arbitrary string to a LineItemType, resolving legacy aliases. */
export function toLineItemType(type: string | null | undefined): LineItemType | null {
  if (!type) return null;
  if (type in LINE_ITEM_TYPE_LABELS) return type as LineItemType;
  return LEGACY_LINE_ITEM_TYPE_ALIASES[type] ?? null;
}

/** Convert a line_item_type enum value to a human label. */
export function getLineItemTypeLabel(type: string | null | undefined): string {
  if (!type) return '';
  const canonical = toLineItemType(type);
  if (canonical) return LINE_ITEM_TYPE_LABELS[canonical];
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Options shaped for `<select>` / picker components. */
export const LINE_ITEM_TYPE_OPTIONS: readonly { value: LineItemType; label: string }[] =
  LINE_ITEM_TYPES.map(value => ({ value, label: LINE_ITEM_TYPE_LABELS[value] }));

/**
 * Types whose `quantity` is PERSON-DAYS — per hand per day — rather than
 * whatever `unit` says.
 *
 * MIRRORS `FLAT_RATE_ONLY_LINE_ITEM_TYPES` in the API's
 * `src/constants/line-item-types.ts`, and has to: payroll.service.ts and
 * pay-ai.service.ts both gate the person-day divisor on the API's list, and a
 * row this screen prices with a different gate is a figure the office approves
 * and payroll then disagrees with, silently. There is no import path between
 * the two repos, so this is a copy by construction; `src/lib/flat-rate-pay.
 * test.ts` pins it against the API's own cases.
 *
 * WHY THIS IS ONE ENTRY AND MUST STAY THAT WAY. `crt`, `thread_wash`,
 * `day_rate` and `standby` all price on a `day_rate_amount` too, but their
 * quantity is metres or jobs (crt lines carry quantity up to 4000, measured
 * 2026-08-25) and each hand on them is owed one FULL day rate regardless of
 * crew size. Widening this list divides two live tenants' approved thread-wash
 * pay by their crew, or multiplies a crt hand's day rate by 3000.
 */
export const FLAT_RATE_ONLY_LINE_ITEM_TYPES: readonly LineItemType[] = [
  'subsistence',
];

/** Is `quantity` on this type person-days? Legacy aliases resolve first. */
export function isPersonDayQuantityType(type: string | null | undefined): boolean {
  const canonical = toLineItemType(type);
  return canonical != null && FLAT_RATE_ONLY_LINE_ITEM_TYPES.includes(canonical);
}
